import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  parseGuestCatalogViewMode,
  type GuestCatalogViewMode,
} from "@/components/guest-catalog/guestCatalogViewMode";

function storageKey(tenantSlug: string) {
  return `guest-opac-view:${tenantSlug}`;
}

function readStoredView(tenantSlug?: string): GuestCatalogViewMode | null {
  if (!tenantSlug || typeof window === "undefined") return null;
  try {
    const value = localStorage.getItem(storageKey(tenantSlug));
    return value === "list" ? "list" : value === "grid" ? "grid" : null;
  } catch {
    return null;
  }
}

export function useGuestCatalogViewMode(tenantSlug?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get("view");

  const viewMode = useMemo(() => {
    if (urlView === "list" || urlView === "grid") {
      return parseGuestCatalogViewMode(urlView);
    }
    return readStoredView(tenantSlug) ?? "grid";
  }, [urlView, tenantSlug]);

  const setViewMode = useCallback(
    (mode: GuestCatalogViewMode) => {
      if (tenantSlug) {
        try {
          localStorage.setItem(storageKey(tenantSlug), mode);
        } catch {
          /* ignore quota / private mode */
        }
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (mode === "grid") next.delete("view");
        else next.set("view", mode);
        return next;
      });
    },
    [tenantSlug, setSearchParams]
  );

  return { viewMode, setViewMode };
}
